import React, { useState, useCallback, useMemo, useContext } from 'react';
import { useMutation } from '@apollo/client';
import Swal from 'sweetalert2';
import useParsedTableData from '../../common/hooks/useParsedTableData';
import ClinicContext from '../../clinics/context/ClinicContext';
import { useDeleteClinicUser } from '../../clinics/redux/hooks';
import UpdateClinicUserDialog from '../../clinics/Management/ClinicDetails/UpdateClinicUserDialog';
import ManageUsersTable from '../../auth/Management/ManageUsersTable';
import { DELETE_USER_MUTATION } from '../../clinics/gql/Mutation';
import clientGraphql from '../../../common/apollo-graphql';

export default function UsersTable({ data, loading, handleOnAdd, minTableHeight, updateRole = true, showToolbar = false, handleFilterChanged = null, useGraphqlDelete = false }) {
  const { id: clinicId, refetch } = useContext(ClinicContext);
  const [userData, setUserData] = useState({});
  const [updateUserDialogVisible, setUpdateUserDialogVisible] = useState(false);
  const [delete_user, { loading: deleteUserMutationLoading }] = useMutation(DELETE_USER_MUTATION, {
    client: clientGraphql,
  });
  const { handleOnDeleteUser, deleteClinicUserPending } = useDeleteClinicUser(clinicId, refetch);

  const handleUpdateUserDialogClose = useCallback((isSuccess = false) => {
    setUpdateUserDialogVisible(false);
    if (isSuccess) {
      refetch();
    }
  }, [refetch]);

  const handleOnEdit = useCallback((userData) => {
    setUserData(userData);
    setUpdateUserDialogVisible(true);
  }, []);

  const handleOnDeleteManageUser = useCallback((_clinicId, userId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This will deactivate the user account and remove all clinic relationships.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Proceed',
    }).then((result) => {
      if (result.isConfirmed) {
        delete_user({
          client: clientGraphql,
          variables: { userId },
        }).then(() => {
          Swal.fire({
            icon: 'success',
            text: 'User is successfully deleted.',
            showConfirmButton: true,
          }).then(() => {
            refetch();
          });
        }).catch((err) => {
          Swal.fire({
            text: (err?.message) || 'Cannot process request.',
            icon: 'error',
            showConfirmButton: true,
          });
        });
      }
    });
  }, [delete_user, refetch]);

  const actions = useMemo(() => {
    return {
      onEdit: handleOnEdit,
      onDelete: useGraphqlDelete ? handleOnDeleteManageUser : handleOnDeleteUser,
    };
  }, [handleOnDeleteManageUser, handleOnDeleteUser, handleOnEdit, useGraphqlDelete]);

  const { parsedData } = useParsedTableData(data, actions);

  return (
    <>
      <ManageUsersTable
        onAdd={handleOnAdd}
        loading={loading || deleteClinicUserPending || deleteUserMutationLoading}
        data={parsedData}
        showToolbar={showToolbar}
        onFilterChanged={handleFilterChanged}
        minTableHeight={minTableHeight}
      />
      <UpdateClinicUserDialog open={updateUserDialogVisible}
        userUuid={userData?.uuid}
        updateRole={updateRole}
        clinicId={clinicId}
        onClose={handleUpdateUserDialogClose} 
      />
    </>
  );
}
